import React from "react";
import { Badge, Card, DataTable, PageHeader, Tabs } from "../components/DesignSystem";
import type { CalendarItem, CalendarItemKind, Scope } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { ErrorState, formatShortDate, LoadingState } from "./common";

const KIND_TONE: Record<CalendarItemKind, "neutral" | "success" | "warning"> = {
  event: "success",
  task: "neutral",
  payment: "warning"
};

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function CalendarScreen({ scope }: { scope: Scope }) {
  const { loading, error, service } = usePlannerData();
  const [view, setView] = React.useState<"month" | "week" | "agenda">("agenda");
  const [kind, setKind] = React.useState<"all" | CalendarItemKind>("all");
  const [items, setItems] = React.useState<CalendarItem[] | null>(null);
  const [itemsLoading, setItemsLoading] = React.useState(false);
  const [itemsError, setItemsError] = React.useState<string | null>(null);
  const [monthCursor, setMonthCursor] = React.useState(() => { const d = new Date(); d.setDate(1); return d; });

  const load = React.useCallback(async () => {
    setItemsLoading(true);
    setItemsError(null);
    try {
      setItems(await service.calendar.getItems());
    } catch (err) {
      setItemsError(errorMessage(err));
    } finally {
      setItemsLoading(false);
    }
  }, [service]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="pob-pad"><LoadingState label="Loading calendar" /></div>;
  if (error) return <div className="pob-pad"><ErrorState message={error} /></div>;

  // Product Excellence Pass 1 §9: honour the same Whole-Wedding/shared/single-event scope every
  // other screen (Events, Tasks, Wave2Screens' scopedRecord) already applies -- Calendar
  // previously ignored the global scope switcher entirely while the persistent "Scoped to X"
  // badge stayed on screen, a visible contradiction between the app's own nav chrome and this
  // screen's actual results.
  const inScope = (item: CalendarItem): boolean => {
    if (scope === "all") return true;
    if (scope === "shared") return !item.eventId;
    return item.eventId === scope || !item.eventId;
  };
  const filtered = (items || []).filter((item) => (kind === "all" || item.kind === kind) && inScope(item));

  return (
    <div>
      <PageHeader
        eyebrow="Plan"
        title="Calendar"
        tabs={<Tabs value={view} onChange={setView} tabs={[{ value: "agenda", label: "Agenda" }, { value: "week", label: "This week" }, { value: "month", label: "Month" }]} />}
      />
      <div className="pob-pad">
        {itemsLoading && !items && <LoadingState label="Loading calendar" />}
        {itemsError && <ErrorState message={itemsError} retry={load} />}

        {items && (
          <>
            <Tabs
              value={kind}
              onChange={setKind}
              tabs={[
                { value: "all", label: "All" },
                { value: "event", label: "Events" },
                { value: "task", label: "Tasks" },
                { value: "payment", label: "Payments" }
              ]}
            />
            <div style={{ marginTop: "var(--space-4)" }}>
              {view === "agenda" && <AgendaView items={filtered} />}
              {view === "week" && <AgendaView items={filtered.filter((item) => isWithinCurrentWeek(item.date))} emptyLabel="Nothing scheduled this week" />}
              {view === "month" && <MonthView items={filtered} cursor={monthCursor} onPrev={() => setMonthCursor(shiftMonth(monthCursor, -1))} onNext={() => setMonthCursor(shiftMonth(monthCursor, 1))} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function isWithinCurrentWeek(dateStr: string): boolean {
  const date = new Date(dateStr);
  const start = startOfWeek(new Date());
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return date >= start && date < end;
}

function shiftMonth(cursor: Date, delta: number): Date {
  const next = new Date(cursor);
  next.setMonth(next.getMonth() + delta);
  return next;
}

function AgendaView({ items, emptyLabel }: { items: CalendarItem[]; emptyLabel?: string }) {
  return (
    <Card padding="0">
      <DataTable
        rows={items}
        emptyLabel={emptyLabel || "Nothing scheduled"}
        columns={[
          { key: "date", header: "Date", width: 110, muted: true, render: (row) => formatShortDate(row.date) },
          { key: "kind", header: "Kind", width: 100, render: (row) => <Badge tone={KIND_TONE[row.kind]}>{row.kind}</Badge> },
          { key: "title", header: "What", wrap: true },
          { key: "status", header: "Status", width: 120, muted: true, render: (row) => row.status || "—" }
        ]}
      />
    </Card>
  );
}

function MonthView({ items, cursor, onPrev, onNext }: { items: CalendarItem[]; cursor: Date; onPrev: () => void; onNext: () => void }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstDay.getDay();
  const byDay = new Map<number, CalendarItem[]>();
  for (const item of items) {
    const d = new Date(item.date);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const list = byDay.get(d.getDate()) || [];
      list.push(item);
      byDay.set(d.getDate(), list);
    }
  }
  const cells: (number | null)[] = [...Array(leadingBlanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <Card padding="var(--space-4)">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-4)" }}>
        <button type="button" onClick={onPrev} style={{ border: 0, background: "transparent", cursor: "pointer", font: "var(--type-label)" }}>← Prev</button>
        <span style={{ font: "var(--type-label)" }}>{cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
        <button type="button" onClick={onNext} style={{ border: 0, background: "transparent", cursor: "pointer", font: "var(--type-label)" }}>Next →</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} style={{ font: "var(--type-eyebrow)", textAlign: "center", color: "var(--text-tertiary)" }}>{d}</div>
        ))}
        {cells.map((day, index) => (
          <div key={index} style={{ minHeight: 64, border: day ? "1px solid var(--divider)" : "none", borderRadius: "var(--radius-sm)", padding: 4 }}>
            {day && (
              <>
                <span style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{day}</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                  {(byDay.get(day) || []).slice(0, 3).map((item) => (
                    <span key={item.id} title={item.title} style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "1px 4px", borderRadius: 3, background: "var(--surface-sunken)" }}>{item.title}</span>
                  ))}
                  {(byDay.get(day) || []).length > 3 && <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>+{(byDay.get(day) || []).length - 3} more</span>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
